import api from './api';

/** Alineado a char(135) en BD y [MaxLength(135)] en NoImputableDto. */
export const MAX_NO_IMPUTABLE_DESCRIPCION_LEN = 135;
/** Alineado a char(60) en BD y [MaxLength(60)] en NoImputableDto. */
export const MAX_NO_IMPUTABLE_NOMBRE_LEN = 60;

export interface NoImputable {
    nroRubro: number;
    descripcioncta: string;
    nombreCuenta?: string;
    segmento: number;
    totalizaEn?: number;
    saldo?: number;
}

export function trimNoImputableDescripcion(v: string | null | undefined): string {
    return String(v ?? '').trim().slice(0, MAX_NO_IMPUTABLE_DESCRIPCION_LEN);
}

/** null si queda vacío; siempre ≤ 60 caracteres. */
export function trimNoImputableNombreCuenta(v: string | null | undefined): string | null {
    const t = String(v ?? '').trim().slice(0, MAX_NO_IMPUTABLE_NOMBRE_LEN);
    return t.length === 0 ? null : t;
}

/** Quita relleno de espacios de columnas `char()` al cargar/editar en pantalla. */
export function trimNoImputableForFormFields(n: NoImputable): NoImputable {
    return {
        ...n,
        descripcioncta: trimNoImputableDescripcion(n.descripcioncta),
        nombreCuenta: trimNoImputableNombreCuenta(n.nombreCuenta) ?? ''
    };
}

/** Cuerpo JSON para POST; `nombreCuenta` null si vacío (válido en API). */
function toApiCreateBody(n: NoImputable) {
    return {
        nroRubro: n.nroRubro,
        descripcioncta: trimNoImputableDescripcion(n.descripcioncta),
        nombreCuenta: trimNoImputableNombreCuenta(n.nombreCuenta),
        segmento: n.segmento,
        totalizaEn:
            n.totalizaEn === undefined || n.totalizaEn === null || Number(n.totalizaEn) === 0 ? null : n.totalizaEn,
        saldo: n.saldo ?? null
    };
}

function toApiUpdateBody(n: Partial<NoImputable>) {
    return {
        descripcioncta: trimNoImputableDescripcion(n.descripcioncta),
        nombreCuenta: trimNoImputableNombreCuenta(n.nombreCuenta),
        segmento: n.segmento ?? 0,
        totalizaEn:
            n.totalizaEn === undefined || n.totalizaEn === null || Number(n.totalizaEn) === 0 ? null : n.totalizaEn,
        saldo: n.saldo ?? null
    };
}

export interface NoImputableSearchParams {
    searchTerm?: string;
    pageNumber: number;
    pageSize: number;
}

export interface NoImputablePagedResult {
    items: NoImputable[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
}

/** Texto para listas y Autocomplete (Nro. rubro = clave en NoImputables). */
export function formatNoImputableLabel(n: Pick<NoImputable, 'nroRubro' | 'nombreCuenta' | 'descripcioncta'>): string {
    const t = (n.nombreCuenta || n.descripcioncta || '').trim();
    return t ? `${n.nroRubro} — ${t}` : String(n.nroRubro);
}

const noImputableService = {
    getNoImputables: async (params: NoImputableSearchParams): Promise<NoImputablePagedResult> => {
        const response = await api.get<NoImputablePagedResult>('/api/NoImputables', { params });
        return response.data;
    },

    getNoImputable: async (nroRubro: number): Promise<NoImputable> => {
        const response = await api.get<NoImputable>(`/api/NoImputables/${nroRubro}`);
        return response.data;
    },

    createNoImputable: async (noImputable: NoImputable): Promise<NoImputable> => {
        const response = await api.post<NoImputable>('/api/NoImputables', toApiCreateBody(noImputable));
        return response.data;
    },

    updateNoImputable: async (nroRubro: number, noImputable: Partial<NoImputable>): Promise<void> => {
        await api.put(`/api/NoImputables/${nroRubro}`, toApiUpdateBody(noImputable));
    },

    deleteNoImputable: async (nroRubro: number): Promise<void> => {
        await api.delete(`/api/NoImputables/${nroRubro}`);
    },

    /** Recorre la paginación del backend (pageSize máx. 20 en API). */
    getAllNoImputablesPlanCuentas: async (): Promise<NoImputable[]> => {
        const pageSize = 20;
        let pageNumber = 1;
        let totalCount = Infinity;
        const rows: NoImputable[] = [];
        const maxPages = 5000;

        while (rows.length < totalCount && pageNumber <= maxPages) {
            const response = await api.get('/api/NoImputables', {
                params: { pageNumber, pageSize, searchTerm: '' }
            });
            const data = response.data;
            const rawItems: NoImputable[] = Array.isArray(data?.items) ? data.items : [];
            if (pageNumber === 1) {
                totalCount = typeof data?.totalCount === 'number' ? data.totalCount : rawItems.length;
            }
            rows.push(...rawItems);
            if (rawItems.length === 0) break;
            pageNumber += 1;
        }

        return rows;
    }
};

export default noImputableService;
